import { useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useContainerFlags, useUpdateContainerFlags } from "@/hooks/useContainerFlags";
import { getApiErrorMessage } from "@/lib/api/api";
import { flagColorClass } from "@/lib/container-flags";
import type { FlagColorKey } from "@/types/container-flag";

interface ContainerFlagSettingsCardProps {
  /** False renders it read-only — a cook may read the code, not rewrite it. */
  canManage: boolean;
}

/** The meaning typed against each colour; blank means the colour is not in use. */
type Draft = Partial<Record<FlagColorKey, string>>;

function draftFrom(codes: { color: FlagColorKey; meaning: string }[] | undefined): Draft {
  const draft: Draft = {};
  for (const code of codes ?? []) draft[code.color] = code.meaning;
  return draft;
}

/**
 * Settings → Container flags: what this kitchen's colours mean.
 *
 * "The flag could be useful, but the flags would need to be customizable"
 * (T-1005, transcript 46:47). A colour with nothing typed against it is not
 * offered anywhere — a dot nobody can read is worse than no dot, because it
 * looks like information.
 */
export function ContainerFlagSettingsCard({ canManage }: ContainerFlagSettingsCardProps) {
  const { t } = useLingui();
  const { data, isLoading, isError } = useContainerFlags();
  const save = useUpdateContainerFlags();

  const [draft, setDraft] = useState<Draft | null>(null);
  // Take the server's code when a new one arrives, rather than in an effect.
  const serverKey = data ? data.codes.map((c) => `${c.color}=${c.meaning}`).join("|") : null;
  const [seen, setSeen] = useState<string | null>(serverKey);
  if (serverKey !== seen) {
    setSeen(serverKey);
    setDraft(null);
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Container flags`}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t`Container flags`}</CardTitle>
          <CardDescription>{t`The colour code could not be loaded.`}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const current = draft ?? draftFrom(data.codes);
  const isDirty = draft != null && serverKey !== null &&
    data.availableColors.some((c) => (current[c] ?? "") !== (draftFrom(data.codes)[c] ?? ""));

  const commit = () => {
    const codes = data.availableColors
      .map((color) => ({ color, meaning: (current[color] ?? "").trim() }))
      .filter((c) => c.meaning !== "");

    save.mutate(codes, {
      onSuccess: () => {
        setDraft(null);
        toast.success(t`Container flag colours saved`);
      },
      onError: (err) => toast.error(getApiErrorMessage(err, t`Failed to save the colour code`)),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t`Container flags`}</CardTitle>
        <CardDescription>
          {t`Say what each colour means in your kitchen. A colour left blank is not offered when flagging a container or a tool.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {data.availableColors.map((color) => (
            <div key={color} className="flex items-center gap-3">
              <span
                className={`inline-block size-6 shrink-0 rounded-full ${flagColorClass(color)}`}
                aria-hidden="true"
              />
              <Label htmlFor={`flag-${color}`} className="w-20 shrink-0 capitalize">
                {color}
              </Label>
              <Input
                id={`flag-${color}`}
                value={current[color] ?? ""}
                maxLength={60}
                disabled={!canManage || save.isPending}
                onChange={(e) => setDraft({ ...current, [color]: e.target.value })}
                placeholder={t`e.g. Contains a Top-9 allergen`}
              />
            </div>
          ))}
        </div>

        {canManage && (
          <div className="flex items-center gap-2">
            <Button onClick={commit} disabled={!isDirty || save.isPending} className="gap-1.5">
              {save.isPending && <Loader2 className="size-3.5 animate-spin" />}
              {t`Save colour code`}
            </Button>
            {isDirty && (
              <Button variant="ghost" disabled={save.isPending} onClick={() => setDraft(null)}>
                {t`Discard`}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
